import * as THREE from 'three';
import { glyph } from './holy.ts';
import type { GlowLayer } from './glow.ts';

/*
 * A paladin's aura on the field (2026-10-30): a ring of light on the ground at the aura's true reach, its look by
 * the aura carried and growing with its rank (I, II, III, by the hero's level like the skills):
 * - devotion: cold silver-blue, a double ring set with small shields (allies inside take less);
 * - retribution: holy embers in dragon red, a ring of flame tongues (who strikes an ally inside is struck back);
 * - life: soft green-white, a ring of leaves (allies inside heal every second).
 * Rank I is the ring alone; II adds a ring of runes turning inside it; III adds pillars of light standing on the
 * rim and a brighter ring. Low and faint on purpose: the nightmares walking through it stay readable. Calm on purpose
 * too (2026-11-01): it breathes slowly and flares at most once every few seconds, softly; life heals every second,
 * and a flare per heal read as a fast, tiring throb. Like holy.ts:
 * textures drawn once, meshes pooled, `begin()`/`end()` around one frame's placing and `frame()` animating;
 * additive, no lights. What it adds to the glow layer (pillars, allies' glints) goes through `glows`.
 */

export type AuraKind = 'devotion' | 'retribution' | 'life';

const canvas = (w: number): [HTMLCanvasElement, CanvasRenderingContext2D] => {
  const c = document.createElement('canvas');
  c.width = c.height = w;
  return [c, c.getContext('2d') as CanvasRenderingContext2D];
};

const tex = (c: HTMLCanvasElement): THREE.Texture => {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
};

const TAU = Math.PI * 2;

/** The rim of each aura, white on clear, with a faint pool of light toward it (one texture, one draw). */
const rims = new Map<AuraKind, THREE.Texture>();
const rimTex = (kind: AuraKind): THREE.Texture => {
  const had = rims.get(kind);
  if (had) return had;
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  // The pool: nothing at the heart, a soft rise toward the rim.
  const gr = g.createRadialGradient(C, C, 0.3 * C, C, C, 0.97 * C);
  gr.addColorStop(0, 'rgba(255,255,255,0)');
  gr.addColorStop(0.75, 'rgba(255,255,255,0.07)');
  gr.addColorStop(1, 'rgba(255,255,255,0.2)');
  g.fillStyle = gr;
  g.beginPath();
  g.arc(C, C, 0.97 * C, 0, TAU);
  g.fill();
  g.strokeStyle = '#fff';
  g.fillStyle = '#fff';
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 10;
  const circle = (r: number, w: number): void => {
    g.lineWidth = w;
    g.beginPath();
    g.arc(C, C, r * C, 0, TAU);
    g.stroke();
  };
  if (kind === 'devotion') {
    // A double ring, set with twelve small shields.
    circle(0.955, 7);
    circle(0.905, 2.5);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU;
      g.save();
      g.translate(C + Math.cos(a) * 0.93 * C, C + Math.sin(a) * 0.93 * C);
      g.rotate(a + Math.PI / 2);
      const w = 0.03 * C, h = 0.04 * C;
      g.beginPath();
      g.moveTo(-w, -h);
      g.lineTo(w, -h);
      g.lineTo(w, 0);
      g.quadraticCurveTo(w, h * 0.8, 0, h * 1.3);
      g.quadraticCurveTo(-w, h * 0.8, -w, 0);
      g.closePath();
      g.fill();
      g.restore();
    }
  } else if (kind === 'retribution') {
    // A ring of flame tongues licking outward, long and short.
    circle(0.9, 5);
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * TAU;
      const len = i % 3 === 0 ? 0.085 : i % 3 === 1 ? 0.05 : 0.035;
      const w = 0.018;
      g.beginPath();
      const p = (r: number, da: number): [number, number] => [C + Math.cos(a + da) * r * C, C + Math.sin(a + da) * r * C];
      g.moveTo(...p(0.9, -w));
      g.quadraticCurveTo(...p(0.9 + len * 0.6, -w * 0.2), ...p(0.9 + len, w * 0.6));
      g.quadraticCurveTo(...p(0.9 + len * 0.4, w * 0.4), ...p(0.9, w));
      g.closePath();
      g.fill();
    }
  } else {
    // A ring of leaves on a fine stem.
    circle(0.93, 3);
    for (let i = 0; i < 20; i++) {
      const a = (i / 20) * TAU;
      g.save();
      g.translate(C + Math.cos(a) * 0.93 * C, C + Math.sin(a) * 0.93 * C);
      g.rotate(a + (i % 2 ? 0.7 : -0.7));
      g.beginPath();
      g.ellipse(0, -0.025 * C, 0.012 * C, 0.03 * C, 0, 0, TAU);
      g.fill();
      g.restore();
    }
    circle(0.875, 1.5);
  }
  const t = tex(c);
  rims.set(kind, t);
  return t;
};

/** Rank II: a ring of runes and dots inside the rim, turning the other way. */
let runesTex: THREE.Texture | null = null;
const runes = (): THREE.Texture => {
  if (runesTex) return runesTex;
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  g.strokeStyle = '#fff';
  g.fillStyle = '#fff';
  g.lineCap = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 8;
  g.lineWidth = 2;
  for (const r of [0.84, 0.74]) {
    g.beginPath();
    g.arc(C, C, r * C, 0, TAU);
    g.stroke();
  }
  g.lineWidth = 2.5;
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * TAU;
    g.save();
    g.translate(C + Math.cos(a) * 0.79 * C, C + Math.sin(a) * 0.79 * C);
    g.rotate(a + Math.PI / 2);
    glyph(g, 0, 0, 0.026 * C, i + 211);
    g.restore();
    const b = a + Math.PI / 24;
    g.beginPath();
    g.arc(C + Math.cos(b) * 0.79 * C, C + Math.sin(b) * 0.79 * C, 3, 0, TAU);
    g.fill();
  }
  runesTex = tex(c);
  return runesTex;
};

const flat = (map: THREE.Texture): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({ map, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });

interface Look {
  rim: THREE.Color;
  runes: THREE.Color;
  /** Pillars, glints and motes. */
  mote: THREE.Color;
  moteHex: string;
}

const LOOKS: Record<AuraKind, Look> = {
  devotion: { rim: new THREE.Color('#9fc0ff'), runes: new THREE.Color('#dce8ff'), mote: new THREE.Color('#cfe0ff'), moteHex: '#e6efff' },
  retribution: { rim: new THREE.Color('#e04532'), runes: new THREE.Color('#e8765e'), mote: new THREE.Color('#ff6a4a'), moteHex: '#ffb59e' },
  life: { rim: new THREE.Color('#8fe0a8'), runes: new THREE.Color('#e2ffe9'), mote: new THREE.Color('#bff5cf'), moteHex: '#eafff0' },
};

/** How bright each rank burns: the rim, the runes. */
const RIM = [0.4, 0.5, 0.62];
const RUNES = [0, 0.32, 0.42];
/** Seconds between two flares of one aura. */
const PULSE_GAP = 6;
/** Pillars of light on the rim at rank III. */
const PILLARS = 6;

interface Ring {
  g: THREE.Group;
  rim: THREE.Mesh;
  runes: THREE.Mesh;
  kind: AuraKind | null;
  rank: number;
  /** When it last pulsed (its aura acted), for the flash. */
  pulse: number;
  phase: number;
}

export class AuraLayer {
  readonly group = new THREE.Group();
  private rings: Ring[] = [];
  private used = 0;
  private now = 0;
  /** A rim far under the ground until the fight's compile ahead is done (it compiles only what is visible). */
  private readonly keep: THREE.Mesh;
  quality: 'high' | 'low' = 'high';
  /**
   * How strong the rings show (1: as always). A path's fight sets it low: there the hero walks alone through a scene
   * built to be read, and the full ring and its runes took a third of a phone's screen (docs/RENDIMIENTO.md is not the
   * reason: the frame is).
   */
  dim = 1;

  constructor() {
    this.keep = new THREE.Mesh(new THREE.CircleGeometry(1, 8), flat(rimTex('devotion')));
    this.keep.position.set(0, -60, 0);
    this.keep.scale.setScalar(0.0001);
    this.keep.frustumCulled = false;
    this.group.add(this.keep);
  }

  set primed(v: boolean) {
    this.keep.visible = !v;
  }

  begin(): void {
    this.used = 0;
  }

  /**
   * Places a paladin's aura this frame at p (its feet), `r` its world radius. Returns its slot (for `pulse`). The
   * pillars of rank III go into `glows`.
   */
  place(id: number, kind: AuraKind, rank: number, p: THREE.Vector3, r: number, glows: GlowLayer): number {
    const i = this.used++;
    const z = this.rings[i] ?? this.make();
    z.phase = id * 1.7;
    if (z.kind !== kind) {
      z.kind = kind;
      const m = z.rim.material as THREE.MeshBasicMaterial;
      m.map = rimTex(kind);
      m.color.copy(LOOKS[kind].rim);
      (z.runes.material as THREE.MeshBasicMaterial).color.copy(LOOKS[kind].runes);
    }
    z.rank = rank;
    z.g.visible = true;
    z.g.position.set(p.x, p.y + 0.04, p.z);
    z.g.scale.set(r, 1, r);
    z.runes.visible = rank >= 2 && this.dim >= 1;
    if (rank >= 3) {
      // Pillars of light standing on the rim, turning slowly with it.
      const look = LOOKS[kind];
      const k = this.flashOf(z);
      const n = this.quality === 'low' ? 3 : PILLARS;
      for (let j = 0; j < n; j++) {
        const a = (j / n) * TAU + this.now * 0.12 + z.phase;
        const x = p.x + Math.cos(a) * r * 0.93, zz = p.z + Math.sin(a) * r * 0.93;
        const b = 0.88 + 0.12 * Math.sin(this.now * 0.9 + j * 1.3 + z.phase);
        glows.add(x, p.y + 0.12, zz, look.mote, 0.55, (0.5 + 0.4 * k) * b * this.dim);
        glows.add(x, p.y + 0.45, zz, look.mote, 0.36, (0.32 + 0.3 * k) * b);
        glows.add(x, p.y + 0.85, zz, look.mote, 0.26, (0.18 + 0.2 * k) * b);
        glows.add(x, p.y + 1.25, zz, look.mote, 0.18, (0.08 + 0.12 * k) * b);
      }
    }
    return i;
  }

  /**
   * The aura in `slot` acted (a heal, a strike back, a blow softened): it flares softly, at most once every
   * PULSE_GAP seconds (life acts every second, devotion on every blow: a flare each time is a throb).
   */
  pulse(slot: number): void {
    const z = this.rings[slot];
    if (z && this.now - z.pulse >= PULSE_GAP) z.pulse = this.now;
  }

  private flashOf(z: Ring): number {
    const t = (this.now - z.pulse) / 1.2;
    return t < 0 || t >= 1 ? 0 : 1 - t;
  }

  end(): void {
    for (let i = this.used; i < this.rings.length; i++) (this.rings[i] as Ring).g.visible = false;
  }

  frame(now: number): void {
    this.now = now;
    for (let i = 0; i < this.used; i++) {
      const z = this.rings[i] as Ring;
      const rank = Math.max(1, Math.min(3, z.rank)) - 1;
      const k = this.flashOf(z);
      // A slow breath (about eight seconds), shallow: alive, not throbbing.
      const breath = 0.93 + 0.07 * Math.sin(now * 0.8 + z.phase);
      (z.rim.material as THREE.MeshBasicMaterial).opacity = Math.min(1, (RIM[rank] as number) * breath + 0.2 * k) * this.dim;
      z.rim.rotation.z = now * (z.kind === 'retribution' ? 0.09 : 0.05);
      // The flare swells the rim a hair.
      const s = 1 + 0.015 * k;
      z.rim.scale.set(s, s, s);
      if (rank >= 1) {
        (z.runes.material as THREE.MeshBasicMaterial).opacity = Math.min(1, (RUNES[rank] as number) * breath + 0.15 * k);
        z.runes.rotation.z = -now * 0.22;
      }
    }
  }

  private make(): Ring {
    const g = new THREE.Group();
    const mesh = (map: THREE.Texture, y: number, order: number): THREE.Mesh => {
      const m = new THREE.Mesh(new THREE.CircleGeometry(1, 64), flat(map));
      m.rotation.x = -Math.PI / 2;
      m.position.y = y;
      m.renderOrder = order;
      g.add(m);
      return m;
    };
    const rim = mesh(rimTex('devotion'), 0, 2);
    const ru = mesh(runes(), 0.005, 2);
    g.visible = false;
    this.group.add(g);
    const z: Ring = { g, rim, runes: ru, kind: null, rank: 1, pulse: -9, phase: 0 };
    this.rings.push(z);
    return z;
  }

  /** The look of an aura (colours for the world's motes and glints). */
  static look(kind: AuraKind): { mote: THREE.Color; moteHex: string } {
    return LOOKS[kind];
  }
}
